// screens-company.tsx - About, Contact, Legal, Attributions template
// Exports: ScreenAbout, ScreenContact, ScreenLegal, ScreenAttributions
import { useRef, Fragment, useState } from 'react';
import { Link } from '../design/router';
import { Glyph, MarketingFooter, MarketingNav } from '../components/ui-shared';
import { SectionNav, SectionNavItem } from '../components/SectionNav';
import { VerifyWidget, useVerifyGate } from '../components/VerifyWidget';
import { apiPostPublic, ApiHttpError } from '../services/api';
import { SITE_KEY_CONTACT, captchaHeader } from '../services/captcha';

export function ScreenAbout() {
  return (
    <div className="ss-screen">
      <MarketingNav />
      <header style={{ padding: '110px clamp(20px, 6vw, 56px) 80px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 60% 50% at 50% -10%, var(--ss-glow), transparent 70%)' }} />
        <div style={{ position: 'relative', maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <span className="ss-eyebrow">About</span>
          <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 8.5vw, 3.25rem)' }}>Making a game should be as easy as opening a tab. Playing one should feel like owning it.</h1>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 17, lineHeight: 1.7, maxWidth: 580, textWrap: 'pretty' }}>
            Soulstack is a small team building the whole stack: the engine, the browser studio, and the
            hosting your games run on. We prove it works by shipping our own games on it first.
          </p>
        </div>
      </header>
      <section style={{ padding: '0 clamp(20px, 6vw, 56px) 90px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            { t: 'Own the whole stack', c: 'Engine, editor, hosting and multiplayer in one codebase. When everything is ours, nothing is someone else\'s problem.' },
            { t: 'Eat our own cooking', c: 'Five of our own games are built and hosted on the platform you get. If it is not good enough for our games, it ships to nobody.' },
            { t: 'Respect the player', c: 'No launchers, no downloads to play, and a real app to install if they want one. A fast first frame on any device with a browser.' },
          ].map((v, i) => (
            <div key={v.t} style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 28, padding: '30px 0', borderTop: '1px solid var(--ss-border-soft)' }}>
              <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 12, color: 'var(--ss-accent)', paddingTop: 4 }}>0{i + 1}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h3 style={{ fontSize: 17, fontWeight: 600 }}>{v.t}</h3>
                <p style={{ fontSize: 14.5, color: 'var(--ss-text-2)', lineHeight: 1.65, textWrap: 'pretty' }}>{v.c}</p>
              </div>
            </div>
          ))}
          <div className="set-card" style={{ marginTop: 48, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 20, padding: '26px 28px' }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>Work with us</h3>
              <p style={{ fontSize: 13.5, color: 'var(--ss-text-3)', marginTop: 5 }}>Engineers and artists who want to own whole problems.</p>
            </div>
            <Link to="/contact" className="ss-btn ss-btn--ghost">Get in touch</Link>
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

/* ── Contact ──────────────────────────────────────────────────────────── */
const CONTACT_TOPICS = ['Sales', 'Support', 'Partnerships', 'Press'] as const;
type ContactTopic = (typeof CONTACT_TOPICS)[number];
// Partnerships has no dedicated inbox yet - Sales is the closest real one
// (matches the sidebar, which only ever lists Sales/Support/Press).
const TOPIC_ADDRESS: Record<ContactTopic, string> = {
  Sales: 'sales@sstk.studio',
  Support: 'support@sstk.studio',
  Partnerships: 'sales@sstk.studio',
  Press: 'press@sstk.studio',
};
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function contactErrorMessage(err: unknown): string {
  if (err instanceof ApiHttpError) {
    if (err.status === 403) return 'Verification failed. Please try again.';
    if (err.status === 422) return 'Some of those details were not accepted. Check them and try again.';
    if (err.status === 429) return 'Too many messages just now. Wait a minute and try again.';
    if (err.status === 503) return 'Verification is temporarily unavailable. Please try again shortly.';
  }
  return 'We could not send your message.';
}

export function ScreenContact() {
  const [sent, setSent] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [topic, setTopic] = useState<ContactTopic>('Sales');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set once a send has failed: the mailto below is then offered as the way out.
  const [failed, setFailed] = useState(false);
  // Soulstack Verify (sk_studio_contact). studio-api redeems the one-use token
  // before it sends anything, so this is real enforcement, not advisory.
  const verify = useVerifyGate(SITE_KEY_CONTACT);

  // The visitor's own mail client, addressed to the same real inbox and filled
  // in with what they typed. Shown only when the request failed or Verify could
  // not run, so a broken check never leaves the form as a dead end.
  const mailto = (() => {
    const body = [`From: ${name.trim()}`, company.trim() ? `Company: ${company.trim()}` : null, '', message.trim(), '', `Reply to: ${email.trim()}`]
      .filter((line) => line !== null)
      .join('\n');
    return `mailto:${TOPIC_ADDRESS[topic]}?subject=${encodeURIComponent(`[${topic}] Message from ${name.trim()}`)}&body=${encodeURIComponent(body)}`;
  })();
  const showFallback = failed || verify.unavailable || verify.stalled;

  const verifyPending = useRef(false);
  async function send() {
    if (verifyPending.current) return;
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Name, email and a message are required.');
      return;
    }
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }

    setError(null);
    verifyPending.current = true;
    setBusy(true);
    try {
      const verificationToken = await verify.verify();
      await apiPostPublic(
        '/contact',
        {
          name: name.trim(),
          email: email.trim(),
          company: company.trim() || undefined,
          topic: topic.toLowerCase(),
          message: message.trim(),
        },
        captchaHeader(verificationToken, SITE_KEY_CONTACT),
      );
      setSent(true);
    } catch (err) {
      setFailed(true);
      setError(contactErrorMessage(err));
    } finally {
      verifyPending.current = false;
      setBusy(false);
      // The token is spent whether or not the send worked.
      verify.renew();
    }
  }

  return (
    <div className="ss-screen">
      <MarketingNav />
      <section style={{ padding: '88px clamp(20px, 6vw, 56px) 110px' }}>
        <div className="ss-grid-collapse" style={{ maxWidth: 980, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 320px', gap: 56, alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 36 }}>
              <span className="ss-eyebrow">Contact</span>
              <h1 className="ss-display" style={{ fontSize: 'clamp(1.9rem, 8vw, 3rem)' }}>Talk to us</h1>
              <p style={{ color: 'var(--ss-text-2)', fontSize: 16 }}>Sales, support or studio partnerships. We reply within one working day.</p>
            </div>
            {sent ? (
              <div className="set-card" style={{ alignItems: 'center', textAlign: 'center', padding: 48, gap: 14 }}>
                <Glyph size={40} />
                <h3 style={{ fontSize: 18 }}>Message sent</h3>
                <p className="hint">Thanks {name.trim()}, it is with our {topic.toLowerCase()} team. We'll reply to {email.trim()} within one working day.</p>
                <button
                  className="ss-btn ss-btn--ghost ss-btn--sm"
                  onClick={() => {
                    setSent(false);
                    setMessage('');
                    setFailed(false);
                  }}
                >
                  Send another
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="ss-field">
                    <span className="ss-field-label">Name</span>
                    <input className="ss-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required />
                  </div>
                  <div className="ss-field">
                    <span className="ss-field-label">Email</span>
                    <input className="ss-input" type="email" placeholder="you@studio.dev" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="ss-field">
                    <span className="ss-field-label">Company</span>
                    <input className="ss-input" placeholder="Optional" value={company} onChange={(e) => setCompany(e.target.value)} />
                  </div>
                  <div className="ss-field">
                    <span className="ss-field-label">Topic</span>
                    <select className="ss-input" value={topic} onChange={(e) => setTopic(e.target.value as ContactTopic)} style={{ appearance: 'none' }}>
                      {CONTACT_TOPICS.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="ss-field">
                  <span className="ss-field-label">Message</span>
                  <textarea className="ss-input" rows={5} placeholder="What are you building?" style={{ resize: 'vertical', fontFamily: 'inherit' }} value={message} onChange={(e) => setMessage(e.target.value)} required />
                </div>
                <VerifyWidget gate={verify} />
                {error && <p style={{ color: 'var(--ss-danger)', fontSize: 13 }} role="alert">{error}</p>}
                {showFallback && (
                  <p className="hint">
                    Prefer email? <a href={mailto} style={{ color: 'var(--ss-text)', fontWeight: 600 }}>Open a message to {TOPIC_ADDRESS[topic]}</a> with your details filled in.
                  </p>
                )}
                <div>
                  <button className="ss-btn ss-btn--primary" onClick={send} disabled={busy}>
                    {busy ? 'Sending…' : 'Send message'}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 110 }}>
            {[
              { t: 'Sales', v: 'sales@sstk.studio' },
              { t: 'Support', v: 'support@sstk.studio' },
              { t: 'Press', v: 'press@sstk.studio' },
            ].map((c) => (
              <div key={c.t} className="set-card" style={{ padding: '18px 20px', gap: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>{c.t}</span>
                <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 12.5, color: 'var(--ss-text)' }}>{c.v}</span>
              </div>
            ))}
            <p style={{ fontSize: 12, color: 'var(--ss-text-3)', lineHeight: 1.6, padding: '4px 6px' }}>
              Building something live? Include your play URL and we can look before we reply.
            </p>
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

/* ── Legal (one template, three documents) ────────────────────────────── */
export interface LegalDoc {
  title: string;
  updated: string;
  sections: [string, string][];
}

export const LEGAL_DOCS: Record<'terms' | 'privacy' | 'aup', LegalDoc> = {
  terms: {
    title: 'Terms of Service', updated: '1 May 2026',
    sections: [
      ['1. The service', 'Soulstack Studio provides browser-based game creation, hosting, installable publishing and multiplayer at sstk.studio. These terms govern every account and every published game.'],
      ['2. Your content', 'You own what you build. By publishing, you grant us the licence needed to host and serve your game to players, and nothing more.'],
      ['3. Acceptable behaviour', 'No unlawful content, no malware, no harassment, no circumventing player pools or fair-play systems. See the Acceptable Use Policy for specifics.'],
      ['4. Plans and billing', 'Paid plans renew monthly. Downgrades unlist games beyond your publish limit; nothing is deleted. Pool overage is billed as listed on the pricing page.'],
      ['5. Termination', 'You may delete your account at any time; a 14-day grace period applies. We may suspend accounts that breach these terms, with notice where lawful.'],
    ],
  },
  privacy: {
    title: 'Privacy Policy', updated: '1 May 2026',
    sections: [
      ['1. What we collect', 'Account details (email, name), project data you create, and gameplay telemetry needed to run multiplayer sessions and show creators their analytics.'],
      ['2. What we never do', 'We do not sell personal data. Player analytics shown to creators are aggregated, never individual profiles.'],
      ['3. Where data lives', 'EU-hosted infrastructure by default. Game sessions run in the region closest to players.'],
      ['4. Your rights', 'Export or delete your data from account settings. UK and EU data protection law applies, including the right of access and erasure.'],
      ['5. Cookies', 'A session cookie for sign-in and a preference cookie for the studio. No third-party advertising trackers, anywhere.'],
    ],
  },
  aup: {
    title: 'Acceptable Use Policy', updated: '1 May 2026',
    sections: [
      ['1. Published games', 'Games must not contain unlawful content, hate material, or content sexualising minors. Age-gate mature themes honestly.'],
      ['2. Fair play', 'Do not ship cheats, exploit the authority model, or interfere with other tenants\' player pools.'],
      ['3. Infrastructure', 'No crypto mining, no abusive automation against the API, no using game servers as general compute.'],
      ['4. Enforcement', 'Violations lead to unlisting, suspension or removal depending on severity. We notify account owners except where prohibited.'],
    ],
  },
};

export type LegalDocKey = keyof typeof LEGAL_DOCS;

function isLegalDocKey(key: string): key is LegalDocKey {
  return key in LEGAL_DOCS;
}

export function ScreenLegal({ doc = 'terms' }: { doc?: string }) {
  const d = isLegalDocKey(doc) ? LEGAL_DOCS[doc] : LEGAL_DOCS.terms;
  const legalDocs: [string, string][] = [['terms', 'Terms'], ['privacy', 'Privacy'], ['aup', 'Acceptable use']];
  return (
    <div className="ss-screen">
      <MarketingNav />
      <section style={{ padding: '80px clamp(20px, 6vw, 56px) 110px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <SectionNav label="Legal documents" orientation="horizontal" style={{ marginBottom: 40 }}>
            {legalDocs.map(([id, label]) => (
              <SectionNavItem key={id} to={'/legal/' + id} active={doc === id} orientation="horizontal" style={{ fontSize: 11, padding: '6px 16px' }}>
                {label}
              </SectionNavItem>
            ))}
          </SectionNav>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36 }}>
            <h1 className="ss-display" style={{ fontSize: 'clamp(1.7rem, 7vw, 2.5rem)' }}>{d.title}</h1>
            <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 11, color: 'var(--ss-text-3)' }}>Effective {d.updated}</span>
          </div>
          <div className="doc-prose">
            {d.sections.map(([h, body]) => (
              <Fragment key={h}>
                <h3>{h}</h3>
                <p>{body}</p>
              </Fragment>
            ))}
            <p style={{ marginTop: 28, fontSize: 13, color: 'var(--ss-text-3)' }}>
              Questions about this document: <Link to="/contact" style={{ color: 'var(--ss-accent)' }}>contact us</Link>.
            </p>
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

/* ── Attributions ─────────────────────────────────────────────────────── */
// Third-party creative assets shipped in Soulstack Studio and the games
// built on it, with their required credit. Keep in sync with the individual
// ATTRIBUTION.md files next to each asset (e.g.
// src/engine/characters/animation/assets/ATTRIBUTION.md) - this page is the
// canonical place those files point readers at.
const THIRD_PARTY_ASSETS = [
  {
    asset: 'Animated Base Character',
    author: 'Quaternius',
    source: 'Poly Pizza',
    sourceUrl: 'https://poly.pizza/',
    licence: 'CC-BY 4.0',
    licenceUrl: 'https://creativecommons.org/licenses/by/4.0/',
    credit: 'Animated Base Character by Quaternius [CC-BY] via Poly Pizza',
    usedIn: 'The shared skeletal character rig used across Soulstack Studio games, including Sunder.',
  },
];

export function ScreenAttributions() {
  return (
    <div className="ss-screen">
      <MarketingNav />
      <header style={{ padding: '110px clamp(20px, 6vw, 56px) 60px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse 60% 50% at 50% -10%, var(--ss-glow), transparent 70%)' }} />
        <div style={{ position: 'relative', maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <span className="ss-eyebrow">Attributions</span>
          <h1 className="ss-display" style={{ fontSize: 'clamp(2rem, 8.5vw, 3.25rem)' }}>Credit where it is due.</h1>
          <p style={{ color: 'var(--ss-text-2)', fontSize: 17, lineHeight: 1.7, maxWidth: 580, textWrap: 'pretty' }}>
            Third-party creative assets used in Soulstack Studio and the games built on it, with the credit each licence requires.
          </p>
        </div>
      </header>
      <section style={{ padding: '0 clamp(20px, 6vw, 56px) 110px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {THIRD_PARTY_ASSETS.map((a) => (
            <div key={a.asset} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '26px 0', borderTop: '1px solid var(--ss-border-soft)' }}>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{a.asset}</h3>
              <p style={{ fontSize: 14, color: 'var(--ss-text-2)', lineHeight: 1.6 }}>{a.usedIn}</p>
              <p style={{ fontFamily: 'var(--ss-mono, monospace)', fontSize: 13, color: 'var(--ss-text-3)', padding: '10px 14px', borderRadius: 8, background: 'var(--ss-panel)', border: '1px solid var(--ss-border-soft)' }}>
                {a.credit}
              </p>
              <p style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>
                By {a.author}, via <a href={a.sourceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--ss-accent)' }}>{a.source}</a>, licensed <a href={a.licenceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--ss-accent)' }}>{a.licence}</a>.
              </p>
            </div>
          ))}
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
